/**
 * widgets/app-shell/ui/UserMenu — authenticated user menu.
 *
 * Shows the current user's display name / email and a logout action.
 * Reads user + logout from the session store via the public barrel.
 *
 * FSD: widgets may import from entities and shared only (not pages/features).
 */

import { useTranslation } from 'react-i18next';
import { LogOut, Monitor, Moon, Settings, Sun, User, Languages } from 'lucide-react';
import { useSessionStore } from '@/entities/session';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import type { Theme } from '@/shared/lib/theme';
import { i18n as sharedI18n, getEnabledLanguages, LANG_STORAGE_KEY } from '@/shared/i18n';
import type { LanguageCode } from '@/shared/i18n';

interface UserMenuProps {
  /** Called after session is cleared — parent handles navigation. */
  onLogout: () => void;
  /** Called when the user clicks the Settings menu item — parent handles navigation. */
  onSettingsClick?: () => void;
  /**
   * Current theme + setter, passed through from the app layer.
   * When supplied, the dropdown gains a theme submenu (visible on phones
   * only — md+ screens already show the header's ThemeToggle).
   */
  theme?: Theme;
  onThemeChange?: (theme: Theme) => void;
}

export function UserMenu({ onLogout, onSettingsClick, theme, onThemeChange }: UserMenuProps) {
  const { t, i18n } = useTranslation('common');
  const user = useSessionStore((s) => s.user);
  const logout = useSessionStore((s) => s.logout);
  const enabledLanguages = getEnabledLanguages();
  const currentLanguage = i18n.language as LanguageCode;

  function handleLanguageSelect(code: LanguageCode) {
    void sharedI18n.changeLanguage(code);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, code);
    } catch {
      // localStorage may be unavailable in some environments
    }
  }

  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : (user?.email ?? t('shell.user.unknown'));

  async function handleLogout() {
    await logout();
    onLogout();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('shell.user.menu')}
          data-testid="user-menu-trigger"
          className="flex items-center gap-2"
        >
          <User className="h-4 w-4" aria-hidden="true" />
          <span className="max-w-[10rem] truncate text-sm">{displayName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {user?.email && (
          <>
            <div className="px-2 py-1.5">
              <p className="text-xs text-muted-foreground" data-testid="user-menu-email">
                {user.email}
              </p>
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        {onSettingsClick && (
          <>
            <DropdownMenuItem
              onClick={onSettingsClick}
              data-testid="user-menu-settings"
              className="cursor-pointer"
            >
              <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('shell.user.settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Theme + language controls — only shown below md, where the header's
            own ThemeToggle/LanguageSwitcher are hidden (`hidden md:flex`).
            Keeps phone users able to switch both without extra chrome. */}
        {onThemeChange && theme && (
          <div className="md:hidden">
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="user-menu-theme-trigger">
                {theme === 'dark' ? (
                  <Moon className="mr-2 h-4 w-4" aria-hidden="true" />
                ) : theme === 'light' ? (
                  <Sun className="mr-2 h-4 w-4" aria-hidden="true" />
                ) : (
                  <Monitor className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {t('theme.toggle')}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    onClick={() => onThemeChange('light')}
                    data-testid="user-menu-theme-light"
                  >
                    <Sun className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('theme.light')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onThemeChange('dark')}
                    data-testid="user-menu-theme-dark"
                  >
                    <Moon className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('theme.dark')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onThemeChange('system')}
                    data-testid="user-menu-theme-system"
                  >
                    <Monitor className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('theme.system')}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            {enabledLanguages.length > 1 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="user-menu-language-trigger">
                  <Languages className="mr-2 h-4 w-4" aria-hidden="true" />
                  {t('language.switcher')}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {enabledLanguages.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        aria-current={currentLanguage === lang.code ? 'true' : undefined}
                        data-testid={`user-menu-language-${lang.code}`}
                      >
                        {lang.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
            <DropdownMenuSeparator />
          </div>
        )}

        <DropdownMenuItem
          onClick={() => void handleLogout()}
          data-testid="user-menu-logout"
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('shell.user.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
